import { ProviderToken, ApplicationRef, Component, Inject, inject, OnInit, Optional, signal, Signal, Type, ChangeDetectionStrategy } from '@angular/core';
import {
  JwtAuthenticationStore,
  SOFTLINE_FEATURE_JWT_AUTHENTICATION,
  UserInfoService,
} from '@softline/auth';
import { SoftAppsUserInfo } from '@softapps/core';
import { equals, Store } from '@softline/core';
import {
  SOFTLINE_CONFIG_EDIT_CONTEXT_COMPONENT,
  SOFTLINE_FEATURE_UTILITIES_PATCH_CONTEXT } from '../../../utilities.shared';
import { PatchContextStore } from '../../patch-context.store';
import {
  MessageBarStore,
  SOFTLINE_FEATURE_MESSAGE_BAR } from '@softline/ui-core';
import { showRequestErrors } from '@softline/application';
import { CommonModule } from '@angular/common';

import { SeparatorComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
    selector: 'lib-context-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule,
    SeparatorComponent,
    TranslatePipe]
})
export class SettingsComponent implements OnInit {
  private close!: (result: any) => void;
  private readonly userInfo = inject<UserInfoService<SoftAppsUserInfo>>(UserInfoService);
  private readonly store = inject(Store);
  /** False in BFF builds: they never register the jwtAuthentication store feature. */
  private readonly canChangeContext = this.store.isRegistered(
    SOFTLINE_FEATURE_JWT_AUTHENTICATION
  );
  protected readonly loading: Signal<boolean | undefined> = this.canChangeContext
    ? this.store.signal(
        SOFTLINE_FEATURE_JWT_AUTHENTICATION,
        JwtAuthenticationStore.getters.loading
      )
    : signal(false);

  constructor(
    @Optional()
    @Inject(SOFTLINE_CONFIG_EDIT_CONTEXT_COMPONENT)
    public components?: Type<Component>[]
  ) {
    // route-level multi providers (shell-scoped module globals) SHADOW root
    // registrations — union both injector levels (dedupe by reference)
    const rootComponents =
      (inject(ApplicationRef).injector.get(
        // legacy string token — cast for the InjectOptions overload
        SOFTLINE_CONFIG_EDIT_CONTEXT_COMPONENT as unknown as ProviderToken<Type<Component>[] | null>,
        null,
        { optional: true }
      )) ?? [];
    this.components = [...new Set([...rootComponents, ...(this.components ?? [])])];
}

  async ngOnInit(): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_UTILITIES_PATCH_CONTEXT,
      PatchContextStore.actions.load
    );
  }

  registerCloseHandler(handler: (result: any) => void): void {
    this.close = handler;
  }

  async onSave(): Promise<void> {
    const context = this.userInfo.value();
    const patchedContext = this.store.get(
      SOFTLINE_FEATURE_UTILITIES_PATCH_CONTEXT,
      PatchContextStore.getters.data
    );
    if (equals(context, patchedContext)) {
      return;
    }
    // TODO: change the context in BFF mode once the backend exposes an endpoint for it
    if (!this.canChangeContext) {
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.error,
        '#UTILITIES.CONTEXT.NOT_SUPPORTED'
      );
      return;
    }
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_JWT_AUTHENTICATION,
        JwtAuthenticationStore.actions.change,
        patchedContext
      );
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        '#UTILITIES.CONTEXT.SUCCESS'
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }
}
